import React from "react";
import { useMappedState } from "redux-react-hook";
import { Formik } from "formik";
import _ from "lodash";

import { FieldMultiSelect } from "src/library/components/Forms";
import type { IStore } from "src/core/shared/store";
import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import {
  IUser,
  useUserApi
} from "src/core/users/api/users.api";

interface UserMultiSelectProps {
  onUsersChange: (data: { selected: string, usersSelected: string[], allUsersSelected: IUser[] }) => void;
  userFilters?: FilterParams<IUser>;
  userPagination?: PaginatedParams<IUser>;
  disabledOptions?: string[];
  labelOverride?: string;
  debounceTime?: number;
  singleSelection?: boolean
}

const defaultPagination: PaginatedParams<IUser> = {
  page: 0,
  limit: 100,
  offset: 0,
  sort: [ [ "updatedAt", "DESC" ] ]
};

export const UserMultiSelect: React.ReactFCWithChildren<UserMultiSelectProps> = ({
  onUsersChange, userFilters, disabledOptions, userPagination, debounceTime, singleSelection, labelOverride
}) => {
  const [ userOptionsState, setUserOptionsState ] = React.useState<Array<{ text: string; value: string }>>([]);
  const [ loadedUsers, setLoadedUsers ] = React.useState<boolean>(false);
  const { terminology } = useMappedState(({ global }: IStore) => ({ terminology: global.settings && global.settings.terminology }));
  const [ usersResponse, getUsers ] = useUserApi("LIST_USERS");
  const [ allUsersSearched, setAllUsersSearched ] = React.useState<IUser[]>([]);

  // On mount, get the users
  React.useEffect(() => {
    if (!loadedUsers) {
      getUsers({
        pagination: userPagination || defaultPagination,
        filters: userFilters || {}
      });
    }
  }, [
    getUsers,
    loadedUsers,
    userFilters,
    userPagination
  ]);

  // Set list of users as user options.
  React.useEffect(() => {
    if (usersResponse.data && usersResponse.data.result) {
      const users = usersResponse.data.result;

      const userValues = users.map(user => ({
        text: `(${user.email}) ${user.firstName} ${user.lastName}`,
        value: user.id.toString()
      }));

      //populate data with all the new data ensuring uniqueness
      setUserOptionsState(prev => {
        const merged = [ ...prev, ...userValues ];

        //hash maps do not allow duplicate keys
        return Array.from(new Map(merged.map(option => [ option.value, option ])).values());
      });

      //keeping tabs on all the users we have found
      setAllUsersSearched(prev => {
        const merged = _.uniqBy([ ...prev, ...users ], "id");

        return merged;
      });
      setLoadedUsers(true);
    }
  }, [ usersResponse ]);

  //for passing into the multiSelect
  const handleSpecificSearch = React.useCallback((search: string | null) => {
    if (search) {
      //get fuzzy searched drops
      getUsers({
        pagination: {
          ...defaultPagination,
          limit: 50
        },
        //search for branch number and name
        filters: {
          ...userFilters,
          $or: [
            { firstName: { $like: `%${search}%` } },
            { lastName: { $like: `%${search}%` } },
            { email: { $like: `%${search}%` } }
          ]
        }
      });
    } else {
      getUsers({
        pagination: userPagination || defaultPagination,
        filters: userFilters || {}
      });
    }
  }, [
    getUsers,
    userFilters,
    userPagination
  ]);

  const label = React.useMemo(() => {
    if (!singleSelection) {
      return `Select ${terminology ? terminology.user.plural.text : "Users"}`;
    } else {
      return `Select ${terminology ? terminology.user.singular.text : "User"}`;
    }
  }, [ singleSelection, terminology ]);

  return (
    <Formik
      initialValues={{ recipientMultipleUsers: undefined }}
      // dont submit as should be used in from above this
      onSubmit={() => null}
    >
      {({ values, handleChange }) => (
        <FieldMultiSelect
          values={values.recipientMultipleUsers}
          name="recipientMultipleUsers"
          loading={usersResponse.pending}
          handleChange={handleChange}
          label={labelOverride ? labelOverride : label}
          options={userOptionsState}
          disabledOptions={disabledOptions || []}
          validateOnChange={false}
          fieldLogic={(affected, oldSelected) => onUsersChange({
            selected: affected,
            usersSelected: oldSelected,
            allUsersSelected: _.compact(_.map(oldSelected, selected => _.find(allUsersSearched, c => c.id.toString() === selected)))
          })}
          handleSpecificSearch={handleSpecificSearch}
          debounceTime={debounceTime}
          singleSelection={singleSelection}
        />
      )}
    </Formik>
  );
};
